import * as React from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";

import {
	CreateAlertResponse,
	MutationCreateAlertArgs,
} from "../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../utils/normalizeErrors";

type CreateAlertMutationResult = {
	createAlert: CreateAlertResponse;
};

export const createAlertMutation = gql`
	mutation CreateAlertMutation(
		$title: String!
		$update: String!
		$scheduledAt: String
		$target: String
		$userIds: [String!]
		$roleTargets: [String!]
		$locationIds: [String!]
		$communityIds: [String!]
		$deliveryMode: String
	) {
		createAlert(
			title: $title
			update: $update
			scheduledAt: $scheduledAt
			target: $target
			userIds: $userIds
			roleTargets: $roleTargets
			locationIds: $locationIds
			communityIds: $communityIds
			deliveryMode: $deliveryMode
		) {
			errors {
				path
				message
			}
			message
		}
	}
`;

interface Props {
	onMessage?: (message: string) => void;
	children: (
		data: {
			submit: (
				values: MutationCreateAlertArgs
			) => Promise<{
				errors: NormalizedErrorMap | null;
				message?: string;
			}>;
			loading: boolean;
			error?: ApolloError;
		}
	) => React.ReactElement | null;
}

const normalizeStringList = (values?: string[] | null): string[] | undefined => {
	if (!Array.isArray(values)) {
		return undefined;
	}

	const normalized = [...new Set(values.map(item => item.trim()).filter(Boolean))];
	return normalized.length > 0 ? normalized : undefined;
};

const normalizeScopedList = (values?: string[] | null): string[] | undefined => {
	if (!Array.isArray(values)) {
		return undefined;
	}

	const normalized = [...new Set(values.map(item => item.trim()).filter(Boolean))];
	if (normalized.length === 0) {
		return undefined;
	}

	const hasAll = normalized.some(item => item.toUpperCase() === "ALL");
	if (hasAll) {
		return ["ALL"];
	}

	return normalized;
};

const normalizeOptional = (value?: string | null): string | undefined => {
	if (!value) {
		return undefined;
	}

	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
};

export const CreateAlertController: React.FC<Props> = ({ children, onMessage }) => {
	const [createAlert, { loading, error }] = useMutation<
		CreateAlertMutationResult,
		MutationCreateAlertArgs
	>(createAlertMutation);

	const submit = async (values: MutationCreateAlertArgs) => {
		const target = normalizeOptional(values.target)?.toUpperCase();
		const deliveryMode = normalizeOptional(values.deliveryMode)?.toUpperCase();
		const userIds = normalizeStringList(values.userIds);
		const roleTargets = normalizeStringList(values.roleTargets);
		const locationIds = normalizeScopedList(values.locationIds);
		const communityIds = normalizeScopedList(values.communityIds);

		const { data } = await createAlert({
			variables: {
				title: values.title,
				update: values.update,
				scheduledAt: normalizeOptional(values.scheduledAt),
				target,
				userIds,
				roleTargets,
				locationIds,
				communityIds,
				deliveryMode,
			},
		});

		const response = data?.createAlert;
		const errors = response?.errors ? normalizeErrors(response.errors) : null;
		const message = response?.message ?? undefined;

		if (message && onMessage) {
			onMessage(message);
		}

		return {
			errors,
			message,
		};
	};

	return children({ submit, loading, error });
};
